import { useState } from "react";

import InputComponent from "V2.0/components/InputComponent";
import { Button } from "V2.0/common/button";
import { useVoiceSettings } from "./useVoiceSettings";
import { Switch } from "V2.0/common/switch";

export default function InactivitySettings(props) {
  const { inactivity, syncAssistant, workspaceId, assistantId, locked } = props;
  const { updateVoiceSettings } = useVoiceSettings({
    syncAssistant,
    workspaceId,
    assistantId,
  });

  const [inactivitySettings, setInactivitySettings] = useState({
    callEndOnInactivityEnabled: inactivity.callEndOnInactivityEnabled,
    callEndTime: inactivity.callEndTime,
    inactivityMessage: inactivity.inactivityMessage,
    inactivityMessageEnabled: inactivity.inactivityMessageEnabled,
    inactivityTime: inactivity.inactivityTime,
  });

  const [errors, setErrors] = useState({
    callEndTime: null,
    inactivityTime: null,
  });

  function handleUpdateInactivitySettings(updatedSetting) {
    setInactivitySettings((prevSettings) => {
      return {
        ...prevSettings,
        ...updatedSetting,
      };
    });
  }

  function handleToggleInactivityMessageEnabled() {
    handleUpdateInactivitySettings({
      inactivityMessageEnabled: !inactivitySettings.inactivityMessageEnabled,
    });
  }

  function handleToggleCallEndOnInactivityEnabled() {
    handleUpdateInactivitySettings({
      callEndOnInactivityEnabled:
        !inactivitySettings.callEndOnInactivityEnabled,
    });
  }

  function handleUpdateInactivityTime(inactivityTime) {
    if (Number(inactivityTime) < 7 && !Boolean(errors.inactivityTime)) {
      handleUpdateError({
        inactivityTime: "A minimum of 7 Seconds is required",
      });
    }

    if (Number(inactivityTime) >= 7 && Boolean(errors.inactivityTime)) {
      handleUpdateError({
        inactivityTime: null,
      });
    }

    handleUpdateInactivitySettings({
      inactivityTime,
    });
  }

  function handleUpdateInactivityMessage(inactivityMessage) {
    handleUpdateInactivitySettings({
      inactivityMessage,
    });
  }

  function handleUpdateCallEndTime(callEndTime) {
    if (Number(callEndTime) < 7 && !Boolean(errors.callEndTime)) {
      handleUpdateError({
        callEndTime: "A minimum of 7 Seconds is required",
      });
    }

    if (Number(callEndTime) >= 7 && Boolean(errors.callEndTime)) {
      handleUpdateError({
        callEndTime: null,
      });
    }
    handleUpdateInactivitySettings({
      callEndTime,
    });
  }

  function handleUpdateError(updatedError) {
    setErrors((prevErrors) => {
      return {
        ...prevErrors,
        ...updatedError,
      };
    });
  }

  const handleSave = async (data) => {
    const payload = {
      inactivity: inactivitySettings,
    };
    updateVoiceSettings(payload);
  };

  const isSaveDisabled = Object.values(errors).some((value) => Boolean(value));

  return (
    <div className="flex flex-col border rounded-lg w-3/5 min-w-[600px] m-4">
      <div className="p-4 bg-gray-50 rounded-t-lg px-6">
        <p className="text-base font-semibold text-gray-900">Inactivity</p>
        <p className="text-sm mt-1 text-gray-500">
          Control how the assistant responds when the user is silent
        </p>
      </div>

      <div className="px-6 p-4 grid grid-cols-3 space-y-8">
        <div className="col-span-3 space-y-4">
          <div className="flex flex-col gap-y-2">
            <div className="flex items-center gap-x-2">
              <p className="text-base font-medium">Speak Inactive Message</p>
              <Switch
                color="emerald"
                checked={inactivitySettings.inactivityMessageEnabled}
                onClick={handleToggleInactivityMessageEnabled}
                disabled={locked}
              />
            </div>
            <p className="text-sm text-gray-500">
              Speak the message, after wait duration has elapsed
            </p>
          </div>

          <div className="grid grid-cols-4 ml-4">
            <div className="col-span-3 xl:col-span-2">
              <InputComponent
                type="number"
                label="Inactive Wait Duration (seconds)"
                description=""
                min={7}
                invalid={Boolean(errors.inactivityTime)}
                error={errors.inactivityTime}
                value={inactivitySettings.inactivityTime}
                onChange={(e) => handleUpdateInactivityTime(e.target.value)}
                disabled={
                  locked || !inactivitySettings.inactivityMessageEnabled
                }
              />
            </div>
            <div className="col-span-3">
              <InputComponent
                type="textArea"
                label="Inactive Message"
                value={inactivitySettings.inactivityMessage}
                onChange={(e) => handleUpdateInactivityMessage(e.target.value)}
                style={{ height: "100px", fontSize: "13px" }}
                disabled={
                  locked || !inactivitySettings.inactivityMessageEnabled
                }
              />
            </div>
          </div>
        </div>

        <div className="col-span-3 space-y-4">
          <div className="flex flex-col gap-y-2">
            <div className="flex items-center gap-x-2">
              <p className="font-medium text-ms">End Call On Silence</p>
              <Switch
                color="emerald"
                checked={inactivitySettings.callEndOnInactivityEnabled}
                onClick={handleToggleCallEndOnInactivityEnabled}
                disabled={locked}
              />
            </div>
            <p className="text-sm text-gray-500">
              End the call, after wait duration has elapsed
            </p>
          </div>
          <div className="grid grid-cols-4 ml-4">
            <div className="col-span-4 md:col-span-3 xl:col-span-2">
              <InputComponent
                min={7}
                type="number"
                invalid={Boolean(errors.callEndTime)}
                error={errors.callEndTime}
                label="End Call Wait Duration"
                value={inactivitySettings.callEndTime}
                disabled={
                  locked || !inactivitySettings.callEndOnInactivityEnabled
                }
                onChange={(e) => handleUpdateCallEndTime(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 rounded-b-lg px-4 py-3 text-right">
        {!locked && (
          <Button
            onClick={handleSave}
            disabled={isSaveDisabled}
          >
            Save
          </Button>
        )}
      </div>
    </div>
  );
}
